import React from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Download, Globe } from 'lucide-react';
import { CodeBlock } from '../components/code/CodeBlock';

export const TransactionHistory: React.FC = () => {
  const queryParameters = [
    { name: 'startDate', type: 'Unix Timestamp', required: false, description: 'Filters transactions from this timestamp (inclusive).', default: '24 hours ago' },
    { name: 'endDate', type: 'Unix Timestamp', required: false, description: 'Filters transactions up to this timestamp (inclusive).', default: 'Current time' },
    { name: 'status', type: 'Integer', required: false, description: 'Filters by transaction status (e.g., 6 = Refunded).', default: 'All statuses' },
    { name: 'reference', type: 'String', required: false, description: 'Filters by transactionReference or merchantRef.', default: '' },
    { name: 'page', type: 'Integer', required: false, description: 'Page number for pagination (minimum 1)', default: '1' },
    { name: 'size', type: 'Integer', required: false, description: 'Items per page (maximum 100)', default: '25' }
  ];

  const headers = [
    { key: 'Authorization', value: '{{private_key}}', required: true, description: 'Merchant\'s private key for authentication.' }
  ];

  const responseFields = [
    { name: 'transactionReference', type: 'string', description: 'Unique reference for the refund transaction.' },
    { name: 'reference', type: 'decimal', description: 'Merchant\'s original order reference.' },
    { name: 'grossAmount', type: 'decimal', description: 'Original transaction amount.' },
    { name: 'requestedAmount', type: 'decimal', description: 'Amount requested by the merchant' },
    { name: 'transactionFee', type: 'decimal', description: 'Processing/Transaction fee deducted' },
    { name: 'settlementAmount', type: 'decimal', description: 'Net amount after fees' },
    { name: 'customerId', type: 'string', description: 'Unique customer identifier (e.g, email address)' },
    { name: 'currencyCode', type: 'string', description: 'Currency code (e.g., NGN/ USD).' },
    { name: 'createdDate', type: 'string', description: 'Timestamp when the transaction was created.' },
    { name: 'status', type: 'integer', description: 'Refund status code (see Status Codes).' },
    { name: 'errorCode', type: 'string', description: 'Error code (0 = success)' }
  ];

  const successResponseJson = `{
  "status": 200,
  "message": "Request successfully processed",
  "data": {
    "content": [
      {
        "transactionReference": "XLs8M1jl0I2dzPYmmzfE",
        "reference": "9e165283-00ac-48e7-813f-e559324e5502",
        "grossAmount": 150.00,
        "requestedAmount": 150.00,
        "transactionFee": 2.25,
        "settlementAmount": 147.75,
        "customerId": "example@email.com",
        "currencyCode": "NGN",
        "createdDate": "2025-03-30T17:01:46.827105",
        "status": 1,
        "errorCode": "0"
      },
      {
        "transactionReference": "MX9KiGstDRW_tpsaB9zp",
        "reference": "bf925674-4430-4374-9e04-4918332ca0e7",
        "grossAmount": 2.00,
        "requestedAmount": 2.00,
        "transactionFee": 0.00,
        "settlementAmount": 2.00,
        "customerId": "test@example.com",
        "currencyCode": "USD",
        "createdDate": "2025-03-30T14:27:20.240288",
        "status": 1,
        "errorCode": "0"
      }
    ],
    "number": 0,
    "size": 25,
    "totalElements": 2,
    "totalPages": 1,
    "last": true,
    "first": true
  },
  "errors": []
}`;


  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-12">
        <h1 className="text-3xl font-bold text-white mb-4">Transaction History</h1>
        
        {/* Introduction */}
        <div className="mb-8">
          <p className="text-white-muted mb-6 leading-relaxed">
            The Transaction History endpoint allows merchants to retrieve a filtered list of past transactions based on date range, status, or reference. 
            This is useful for reconciliation, reporting, and auditing purposes.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <Search className="h-5 w-5 text-blue-500 mr-2" />
                <h3 className="text-lg font-semibold text-blue-500">Advanced Search</h3>
              </div>
              <ul className="text-sm text-blue-500/90 space-y-2">
                <li>• Date range filtering</li>
                <li>• Status-based queries</li>
                <li>• Amount range searches</li>
                <li>• Customer transaction lookup</li>
              </ul>
            </div>
            
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <Filter className="h-5 w-5 text-green-500 mr-2" />
                <h3 className="text-lg font-semibold text-green-500">Flexible Filtering</h3>
              </div>
              <ul className="text-sm text-green-500/90 space-y-2">
                <li>• Multiple filter combinations</li>
                <li>• Currency-specific queries</li>
                <li>• Payment method filtering</li>
                <li>• Merchant reference lookup</li>
              </ul>
            </div>
            
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <Download className="h-5 w-5 text-purple-500 mr-2" />
                <h3 className="text-lg font-semibold text-purple-500">Export Ready</h3>
              </div>
              <ul className="text-sm text-purple-500/90 space-y-2">
                <li>• Pagination support</li>
                <li>• Bulk data retrieval</li>
                <li>• Structured JSON response</li>
                <li>• Summary statistics</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Endpoint */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Endpoint</h2>
          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="flex items-center">
              <span className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-mono mr-3">GET</span>
              <code className="text-lime font-mono">
                /transaction/history?startDate={'{startDate}'}&endDate={'{endDate}'}&status={'{status}'}&reference={'{reference}'}&page={'{page}'}&size={'{size}'}
              </code>
            </div>
          </div>
        </div>

        {/* Headers */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Headers</h2>
          
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Key</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Value</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Required</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {headers.map((header, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-lime">{header.key}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-white-muted">{header.value}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Yes
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-white-muted">{header.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Query Parameters */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Query Parameters</h2>
          <p className="text-white-muted mb-4">All query parameters are optional and can be combined for advanced filtering:</p>
          
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Parameter</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Default</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {queryParameters.map((param, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-lime">{param.name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-white-muted">{param.type}</td>
                      <td className="px-4 py-4 text-sm text-white-muted">{param.description}</td>
                      <td className="px-4 py-4 text-sm text-white-muted">{param.default}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Sample Response */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Sample Response</h2>
          <CodeBlock code={successResponseJson} language="json" />
        </div>

        {/* Response Fields */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Response Fields</h2>
          
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Field</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {responseFields.map((field, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-lime">{field.name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-white-muted">{field.type}</td>
                      <td className="px-4 py-4 text-sm text-white-muted">{field.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>


        {/* What's Next */}
        <div className="bg-lime text-dark rounded-lg p-6 text-center">
          <div className="flex items-center justify-center mb-3">
            <Globe className="h-6 w-6 mr-2" />
            <h2 className="text-xl font-bold">What's Next</h2>
          </div>
          <p className="text-dark/90 mb-4">
            Continue with webhook configuration for real-time transaction notifications
          </p>
          <Link
            to="/server-to-server/webhook"
            className="inline-flex items-center px-6 py-2 bg-dark text-white font-medium rounded-lg hover:bg-dark/90 transition-colors"
          >
            Webhook
          </Link>
        </div>
      </div>
    </div>
  );
};